import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SavedSearch } from '@pf/shared';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';

export interface CreateSavedSearchBody {
  name: string;
  query?: string;
  filters?: Record<string, unknown>;
  searchType?: 'reporter' | 'contact' | 'publication';
}

export type UpdateSavedSearchBody = Partial<CreateSavedSearchBody>;

const SAVED_SEARCHES_KEY = 'saved-searches';

export function useSavedSearches(searchType?: 'reporter' | 'contact' | 'publication') {
  return useQuery({
    queryKey: [SAVED_SEARCHES_KEY, searchType],
    queryFn: () =>
      apiGet<{ savedSearches: SavedSearch[] }>('/saved-searches', searchType ? { searchType } : undefined),
  });
}

export function useCreateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSavedSearchBody) =>
      apiPost<{ savedSearch: SavedSearch }>('/saved-searches', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SAVED_SEARCHES_KEY] });
    },
  });
}

export function useUpdateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSavedSearchBody }) =>
      apiPatch<{ savedSearch: SavedSearch }>(`/saved-searches/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SAVED_SEARCHES_KEY] });
    },
  });
}

export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/saved-searches/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SAVED_SEARCHES_KEY] });
    },
  });
}
